/**
 * FHIR ↔ UI mapping for addendum DocumentReferences.
 *
 * Provides helpers to:
 * - create a new addendum DocumentReference from UI data
 * - search/load addendums for an appointment or note
 * - map a DocumentReference back to the UI Addendum shape
 */

import type { DocumentReference } from '@medplum/fhirtypes';
import type { MedplumClient } from '@medplum/core';
import { fhirCreateDocumentReference } from '../utils/fhirWrite';
import type { Addendum, AddendumReasonCode } from './types';
import type { AddendumPayload } from './fhirAddendumTypes';
import {
  ADDENDUM_TYPE_CODING,
  ADDENDUM_CATEGORY_CODING,
  ADDENDUM_TYPE_CODE,
  DOC_TYPE_SYSTEM,
  ADDENDUM_ATTACHMENT_CONTENT_TYPE,
} from './fhirAddendumConstants';
import { APPOINTMENT_TAG_SYSTEM, appointmentTag } from '../fhirNoteMapping';
import type { AddendumLineChange } from './types';

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

export interface CreateAddendumParams {
  patientId: string;
  appointmentId: string;
  questionnaireResponseId: string;
  encounterId?: string;
  authorRef?: string;
  authorDisplay?: string;
  body: string;
  reasonForChange: AddendumReasonCode;
  sequenceNumber: number;
  authorName: string;
  authorCredentials?: string;
  originalNoteDate: string;
  lineChanges?: AddendumLineChange[];
  originalLines?: string[];
  afterLines?: string[];
}

/**
 * Build a DocumentReference resource representing a single addendum.
 * Does NOT persist — caller must `medplum.createResource(...)`.
 */
export function buildAddendumDocumentReference(
  params: CreateAddendumParams,
): DocumentReference {
  const payload: AddendumPayload = {
    sequenceNumber: params.sequenceNumber,
    reasonForChange: params.reasonForChange,
    authorName: params.authorName,
    authorCredentials: params.authorCredentials,
    originalNoteDate: params.originalNoteDate,
    lineChanges: params.lineChanges,
    originalLines: params.originalLines,
    afterLines: params.afterLines,
  };

  const payloadJson = JSON.stringify(payload);
  const payloadBase64 =
    typeof btoa === 'function'
      ? btoa(unescape(encodeURIComponent(payloadJson)))
      : Buffer.from(payloadJson, 'utf-8').toString('base64');

  const now = new Date().toISOString();

  const docRef: DocumentReference = {
    resourceType: 'DocumentReference',
    status: 'current',
    type: ADDENDUM_TYPE_CODING,
    category: [ADDENDUM_CATEGORY_CODING],
    subject: {
      reference: `Patient/${params.patientId}`,
    },
    date: now,
    description: params.body || `Revised Note #${params.sequenceNumber}`,
    author: params.authorRef
      ? [{
          reference: params.authorRef,
          ...(params.authorDisplay ? { display: params.authorDisplay } : {}),
        }]
      : params.authorDisplay
        ? [{ display: params.authorDisplay }]
        : undefined,
    content: [
      {
        attachment: {
          contentType: ADDENDUM_ATTACHMENT_CONTENT_TYPE,
          data: payloadBase64,
        },
      },
    ],
    context: {
      related: [
        { reference: `QuestionnaireResponse/${params.questionnaireResponseId}` },
      ],
      encounter: params.encounterId
        ? [{ reference: `Encounter/${params.encounterId}` }]
        : undefined,
    },
    meta: {
      tag: [appointmentTag(params.appointmentId)],
    },
  };

  return docRef;
}

// ---------------------------------------------------------------------------
// Search / Load
// ---------------------------------------------------------------------------

/** True if this DocumentReference is a persisted progress-note addendum (matches create shape). */
export function isProgressNoteAddendumDocumentReference(
  doc: DocumentReference,
): boolean {
  return Boolean(
    doc.type?.coding?.some(
      (c) => c.system === DOC_TYPE_SYSTEM && c.code === ADDENDUM_TYPE_CODE,
    ),
  );
}

/**
 * Load all addendum DocumentReferences for an appointment from Medplum.
 *
 * Query uses **`patient`** (FHIR search param for DocumentReference subject) + `_tag`
 * for the appointment id. We do not use `type` in the query — token search on custom
 * coding systems is unreliable; we filter with `isProgressNoteAddendumDocumentReference`.
 *
 * If `patient` returns nothing (some clients), retries with `subject`.
 */
export async function loadAddendumsForAppointment(
  medplum: MedplumClient,
  patientId: string,
  appointmentId: string,
): Promise<DocumentReference[]> {
  const tagParam = `${APPOINTMENT_TAG_SYSTEM}|${appointmentId}`;
  const base = { _tag: tagParam, _count: '100' } as const;

  let candidates = await medplum.searchResources('DocumentReference', {
    ...base,
    patient: `Patient/${patientId}`,
  });

  if (candidates.length === 0) {
    candidates = await medplum.searchResources('DocumentReference', {
      ...base,
      subject: `Patient/${patientId}`,
    });
  }

  return candidates.filter(isProgressNoteAddendumDocumentReference);
}

/**
 * Load all addendum DocumentReferences for a specific note (QR) from Medplum.
 * Falls back to appointment-level search if `related` search param is unavailable.
 */
export async function loadAddendumsForNote(
  medplum: MedplumClient,
  patientId: string,
  appointmentId: string,
  questionnaireResponseId: string,
): Promise<DocumentReference[]> {
  const allForAppointment = await loadAddendumsForAppointment(
    medplum,
    patientId,
    appointmentId,
  );

  return allForAppointment.filter((dr) =>
    dr.context?.related?.some(
      (r) => r.reference === `QuestionnaireResponse/${questionnaireResponseId}`,
    ),
  );
}

// ---------------------------------------------------------------------------
// Map DocumentReference → UI Addendum
// ---------------------------------------------------------------------------

/**
 * Decode the structured payload from a DocumentReference's attachment.
 * Returns null if decoding fails (graceful degradation).
 */
export function decodeAddendumPayload(
  docRef: DocumentReference,
): AddendumPayload | null {
  const data = docRef.content?.[0]?.attachment?.data;
  if (!data) return null;
  try {
    const json =
      typeof atob === 'function'
        ? decodeURIComponent(escape(atob(data)))
        : Buffer.from(data, 'base64').toString('utf-8');
    return JSON.parse(json) as AddendumPayload;
  } catch {
    return null;
  }
}

/**
 * Map a persisted addendum DocumentReference to the UI Addendum shape.
 * Falls back to description/date when the structured payload is missing.
 */
export function documentReferenceToAddendum(
  docRef: DocumentReference,
): Addendum {
  const payload = decodeAddendumPayload(docRef);

  const authorDisplay = docRef.author?.[0]?.display;

  return {
    id: docRef.id ?? `dr-${Date.now()}`,
    label: payload
      ? `Revised Note #${payload.sequenceNumber}`
      : 'Revised Note',
    authorName: payload?.authorName ?? authorDisplay ?? 'Provider',
    authorCredentials: payload?.authorCredentials,
    timestamp: docRef.date ?? new Date().toISOString(),
    originalNoteDate:
      payload?.originalNoteDate ?? docRef.date ?? new Date().toISOString(),
    body: docRef.description ?? '',
    reasonForChange:
      (payload?.reasonForChange as AddendumReasonCode) ?? 'other',
    lineChanges: payload?.lineChanges,
    originalLines: payload?.originalLines,
    afterLines: payload?.afterLines,
  };
}

/**
 * Map an array of DocumentReferences to sorted UI Addendums.
 */
export function documentReferencesToAddendums(
  docRefs: DocumentReference[],
): Addendum[] {
  return docRefs
    .map(documentReferenceToAddendum)
    .sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );
}

// ---------------------------------------------------------------------------
// Persist (create addendum)
// ---------------------------------------------------------------------------

/**
 * Create and persist an addendum DocumentReference to Medplum.
 * Returns the created resource with server-assigned id.
 */
export async function createAddendum(
  medplum: MedplumClient,
  params: CreateAddendumParams,
): Promise<DocumentReference> {
  const docRef = buildAddendumDocumentReference(params);
  return fhirCreateDocumentReference(medplum, docRef);
}
